import type { LucideIcon } from "lucide-react";
import type { ComponentType } from "react";
import type { Stage } from "../types";

/** A `data-onboarding-target` name on a production element. */
export type Anchor = string;
export type Side = "top" | "right" | "bottom" | "left";
export type Align = "start" | "center" | "end";
/** The canonical sticky papers (`--sticky-*`). */
export type Tint =
  | "butter"
  | "apricot"
  | "meadow"
  | "sea"
  | "orchid"
  | "rose"
  | "mist"
  | "oat";

/**
 * `shell` anchors are tracked continuously from the layer's activation;
 * `route` anchors also read as absent again on every pathname change, so
 * entering a route is a fresh appearance even when the node persisted.
 */
export type TriggerScope = "route" | "shell";

export type Trigger =
  | { kind: "appears"; target: Anchor; scope?: TriggerScope }
  | { kind: "disappears"; target: Anchor; scope?: TriggerScope };

/** Runs before a card shows, only while `when` holds. */
export interface Beat {
  /** What the ring holds during the beat. */
  target: Anchor;
  /**
   * Both run only while the card's own target is missing; `target-collapsed`
   * also needs the beat target to say `aria-expanded="false"`, for a toggle
   * that is the same control open or shut.
   */
  when: "card-target-missing" | "target-collapsed";
  /** ms the ring rests on `target`. */
  hold?: number;
  /** The simulated click on `target`. */
  then?: "click";
  /** ms before the card appears. */
  settle?: number;
}

export type Key = string | { label: string; keys: string[] };

export interface Card {
  key: string;
  icon: LucideIcon;
  target: Anchor;
  side: Side;
  align: Align;
  /** A region the card must not cover. */
  clear?: Anchor;
  /** The same, as a selector, for a region with no anchor of its own (the nodes the agent places). */
  clearSelector?: string;
  before?: Beat;
  title: string;
  body: string;
  keys?: Key[];
  figure?: ComponentType;
  /** Rendered only when `DOCS_BASE` is set. */
  docs?: { path: string; label: string };
  /** Default `next`. */
  advance?: "next" | "target-gone";
  /** ms the card waits for its target before it is skipped (default 1500). */
  wait?: number;
  /** False: the card is skipped as it comes up, with no beat and no wait. */
  applies?: (facts: ShellFacts) => boolean;
}

export interface Moment {
  key: string;
  /** Folds into the persisted key when > 1. */
  version?: number;
  title: string;
  /** Documentation, never rendered. */
  fires: string;
  /** Omitted = chain-only. */
  when?: Trigger;
  /** Fires after these moments complete, if its first card's target is on screen. */
  after?: string | string[];
  /** Its own trigger counts only once these moments are seen. */
  requires?: string | string[];
  /** Fired by name from a flow that owns its timing (the first run's landing); never by a trigger or the chain. */
  manual?: true;
  /** Kernel-internal exceptions. */
  unless?: (facts: ShellFacts) => boolean;
  /** Solo moments declare their paper; sequences use the step mapping. */
  tint?: Tint;
  cards: Card[];
}

export interface ShellFacts {
  pathname: string;
  canvasId: string | null;
  tourCanvasId: string | null;
  stage: Stage;
  /** The footer's usage meter renders (TokenMeter's own two conditions). */
  usageMeter: boolean;
}

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  /** The target's own corner radius; the ring follows it. */
  radius: number;
}

export const BEAT_HOLD_MS = 700;
export const BEAT_SETTLE_MS = 400;
/** How long a card waits for its target before it is skipped, and how long a vanished target may stay gone before the moment pauses. */
export const TARGET_WAIT_MS = 1500;
export const CHAIN_DELAY_MS = 400;
export const STEP_TINTS: readonly Tint[] = [
  "butter",
  "apricot",
  "meadow",
  "sea",
];
